import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HOT_KEY_LABEL_TOGGLE_MICRO, Icon } from '@web-chat-core/ui-kit';
import { observer } from 'mobx-react-lite';
import { useUserSettingsContext } from 'contexts';
import { MutedSpeechTooltip } from 'features/MutedSpeechDetection/components/MutedSpeechTooltip';
import { ObstacleIds } from 'features/MutedSpeechDetection/guardController';
import { ControlButton } from '../ControlButton/ControlButton';
import { MenuItem } from '../ControlButtonMenu/ControlButtonMenu';
import { MicrophoneOffIcon, MicWithWave } from './view';
import { MicrophoneWaveOverlay } from './MicrophoneWaveOverlay';
import { buildMenu } from './helper';

export interface AudioButtonProps {
    onClick?: () => void;
    onAudioInputSourceSelected?: (device: MediaDeviceInfo) => void;
    onAudioOutputSourceSelected?: (device: MediaDeviceInfo) => void;
    isPreJoinPage?: true;
    isButtonDisabled?: boolean;
    isPushToTalkEnabled?: boolean;
    isToggleMicEnabled?: boolean;
    isAdvancedSoundAndCameraSettingsEnabled?: boolean;
    isSpeakerAnimationsEnabled?: boolean;
}

export const AudioButton = observer(
    ({
        onClick,
        onAudioInputSourceSelected,
        onAudioOutputSourceSelected,
        isPreJoinPage,
        isButtonDisabled,
        isPushToTalkEnabled = false,
        isToggleMicEnabled = false,
        isAdvancedSoundAndCameraSettingsEnabled = false,
        isSpeakerAnimationsEnabled = false,
    }: AudioButtonProps) => {
        const { t } = useTranslation();
        const {
            audioEnabled,
            audioInputDevices,
            audioInputDeviceId,
            audioOutputDevices,
            audioOutputDeviceId,
            permissions,
        } = useUserSettingsContext();

        const menuItems = buildMenu({
            isAdvanced: isAdvancedSoundAndCameraSettingsEnabled,
            audioInputDevices,
            audioOutputDevices,
            audioInputDeviceId,
            audioOutputDeviceId,
        });

        const handleMenuItem = useCallback(
            (item: MenuItem) => {
                if (item.kind === 'audioinput') {
                    const inputDevice = audioInputDevices.find((d) => d.label === item.text);
                    if (inputDevice && onAudioInputSourceSelected) {
                        onAudioInputSourceSelected(inputDevice);
                    }
                    return;
                }
                if (item.kind === 'audiooutput') {
                    const outputDevice = audioOutputDevices.find((d) => d.label === item.text);
                    if (outputDevice && onAudioOutputSourceSelected) {
                        onAudioOutputSourceSelected(outputDevice);
                    }
                    return;
                }
            },
            [audioInputDevices, audioOutputDevices, onAudioInputSourceSelected, onAudioOutputSourceSelected],
        );

        const handleClick = useCallback(() => {
            onClick?.();
        }, [onClick]);

        const toggleAudioText = t(audioEnabled ? 'web_call_microphone_turn_off' : 'web_call_microphone_turn_on', {
            0: HOT_KEY_LABEL_TOGGLE_MICRO,
            1: isToggleMicEnabled && !isPreJoinPage ? `/ ${t('vks_call_microphone_tap_space')}` : '',
            2: isPushToTalkEnabled && !isPreJoinPage ? `\n${t('web_call_microphone_hold_space')}` : '',
        });
        const title = permissions.audio ? toggleAudioText : t('web_call_access_site');

        return (
            <MutedSpeechTooltip>
                <ControlButton
                    menuId={ObstacleIds.ControlAudioMenu}
                    active={audioEnabled}
                    title={title}
                    disabled={isButtonDisabled}
                    onClick={handleClick}
                    menuItems={menuItems}
                    onMenuItemClick={handleMenuItem}
                    withoutMenuShadow
                >
                    {audioEnabled ? (
                        <MicWithWave>
                            <Icon icon="icMicrophoneOn" colorVariant="bright" />
                            {isSpeakerAnimationsEnabled && <MicrophoneWaveOverlay />}
                        </MicWithWave>
                    ) : (
                        <MicrophoneOffIcon icon="icMicrophoneOff" />
                    )}
                </ControlButton>
            </MutedSpeechTooltip>
        );
    },
);
